import cfHoy from '@clickfunnels2/cfhoy.js'
import { Cookies } from '@clickfunnels2/cfhoy.js'

function cfhoyVisitorData() {
  return {
    firstName: Cookies.get('contact_first_name'),
    lastName: Cookies.get('contact_last_name'),
    emailAddress: Cookies.get('contact_email_address'),
    phoneNumber: Cookies.get('contact_phone_number'),
    postalCode: Cookies.get('contact_postal_code'),
    country: Cookies.get('contact_country'),
    uuid: Cookies.get('cfhoy_visitor'),
  }
}

function getLeadEventID() {
  return Cookies.get('cfhoy_lead_event_id')
}

function removeLeadEventID() {
  Cookies.set('cfhoy_lead_event_id', '', -1)
  return true
}

function getPurchaseEventID() {
  return Cookies.get('cfhoy_purchase_event_id')
}

function removePurchaseEventID() {
  Cookies.set('cfhoy_purchase_event_id', '', -1)
  return true
}

function getRenderedPage() {
  return document.querySelector('meta[name="rendered-page-url"]')?.getAttribute('content')
}

const getDomain = () => {
  // This is a naive approach meant to hold us over until we can use the official solution
  // Use window.cookie_domain once https://github.com/clickfunnels2/admin/pull/23993 is merged
  const parts = window.location.hostname.split('.')
  const countToKeep = parts.length > 3 ? 3 : 2
  return parts.slice(parts.length - countToKeep).join('.')
}
let livePage = isLivePage(window.location.search)
let trackingDisabled = window.disableTracking

const renderedPage = getRenderedPage()
// we need to configure cfHoy before DOMContentLoaded event
// so it can track the visit correctly
// We need to configure cfHoy even when tracking is disabled, so cfHoy knows not to startOnReady (which is the default behavior)
cfHoy.configure({
  urlPrefix: '/_cf',
  visitsUrl: '/s',
  eventsUrl: '/a',
  platform: 'Web',
  useBeacon: true,
  startOnReady: false,
  trackVisits: true,
  cookies: true,
  withCredentials: false,
  visitDuration: 4 * 60, // 4 hours
  visitorDuration: 2 * 365 * 24 * 60, // 2 years
  page: renderedPage,
  trackPageViewWithVisit: true,
  trackPageViewWithVisitAdditionalProps: () => ({
    cfhoy_visitor: Cookies.get('cfhoy_visitor') || '',
    request_type: 'visit',
  }),
  extendedInstrumentation: true,
})

// When an SDK-hosted page redirects here, it appends __cf_token to the URL
// so the destination origin can recover identity + visit continuity. We
// resolve the token against sdk.<root>/resolve_token, hand the resolved
// {visitId, visitorId} to cfhoy via adoptVisit, and strip the token from
// the URL. The resolve is gated in front of cfHoy.start() so cfhoy lands
// in its "Active visit" branch and skips the visit POST.
const CF_TOKEN_PARAM = '__cf_token'

function getCfTokenFromUrl() {
  return new URLSearchParams(window.location.search).get(CF_TOKEN_PARAM)
}

function stripCfTokenFromUrl() {
  const url = new URL(window.location.href)
  if (!url.searchParams.has(CF_TOKEN_PARAM)) return
  url.searchParams.delete(CF_TOKEN_PARAM)
  window.history.replaceState({}, '', url.toString())
}

function pageUrlWithoutQuery() {
  const url = new URL(window.location.href)
  url.search = ''
  url.hash = ''
  return url.toString()
}

// Same-origin: track_events.js runs on the workspace host (e.g.
// myteam.myclickfunnels.test), and the workspace-host /_cf/resolve_token
// route (config/routes/events.rb) sets signed _cf_aff / _cf_aff_start
// cookies on this host when the token carries aff_code. A cross-origin
// call to sdk.<root> couldn't set workspace-host cookies — that's why
// this is mounted on the workspace host.
function workspaceResolveTokenUrl() {
  return '/_cf/resolve_token'
}

async function resolveCfTokenIfPresent() {
  const token = getCfTokenFromUrl()
  if (!token) return

  try {
    const response = await fetch(workspaceResolveTokenUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ cf_token: token, url: pageUrlWithoutQuery() }),
    })

    // Strip the token from the URL whether resolve succeeded or not —
    // leaving a used/invalid token in the URL serves no purpose and risks
    // leaking via referrer headers or browser history.
    stripCfTokenFromUrl()

    if (!response.ok) return
    const payload = await response.json()
    if (!payload.contact_uuid || !payload.visit_uuid) return

    // cfhoy will write cfhoy_visit / cfhoy_visitor / cfhoy_successful_visit_track
    // when it sees adoptVisit, and skip the visit POST so the source origin's
    // first-touch visit values are preserved.
    cfHoy.configure({
      adoptVisit: { visitId: payload.visit_uuid, visitorId: payload.contact_uuid },
    })
  } catch {
    stripCfTokenFromUrl()
    // Swallow — failure here just means the destination origin starts a
    // fresh visit, same as if the token were never minted.
  }
}

if (!trackingDisabled && livePage) {
  const startCfHoy = () => {
    if (window._cfHoyStarted) return
    window._cfHoyStarted = true
    cfHoy.start()
  }

  // Resolve __cf_token (if any) BEFORE cfHoy.start() runs, so cfhoy sees
  // the adoptVisit config and lands in its "Active visit" branch. cfhoy
  // already supports a deferred start via `startOnReady: false` above, so
  // this isn't a hack — we're just hooking into the existing manual-start
  // path with a promise gate.
  const resolveAndStart = () => resolveCfTokenIfPresent().finally(startCfHoy)

  if (document.readyState === 'complete') {
    resolveAndStart()
  } else {
    window.addEventListener('load', resolveAndStart)
  }

  document.addEventListener('cfhoy:tracked', function (event) {
    window.dataLayer = window.dataLayer || []
    if (event.detail.name == '$view') {
      const visitorData = cfhoyVisitorData()
      dataLayer.push({
        event: 'cfPageView',
        contactFirstName: visitorData.firstName,
        contactLastName: visitorData.lastName,
        contactEmailAddress: visitorData.emailAddress,
        contactPhoneNumber: visitorData.phoneNumber,
        contactPostalCode: visitorData.postalCode,
        contactCountry: visitorData.country,
        contactExternalID: visitorData.uuid,
        visitEventID: event.detail.id,
      })
    }
  })
  document.addEventListener('DOMContentLoaded', function (event) {
    const additionalProps = {
      cfhoy_visitor: Cookies.get('cfhoy_visitor') || '',
      request_type: 'trackView',
    }
    cfHoy.trackView(additionalProps)

    $(document).on('click', '[event-tracking="enable"]', function () {
      const data = {
        url: window.location.href,
        page: renderedPage || window.location.pathname,
        title: document.title,
      }

      cfHoy.track('$button_click', data)
    })
  })
}

function isLivePage(queryString) {
  const urlParams = new URLSearchParams(queryString)
  return urlParams.get('preview') !== 'true'
}

document.addEventListener('DOMContentLoaded', function (event) {
  window.dataLayer = window.dataLayer || []

  const visitorData = cfhoyVisitorData()
  const leadEventID = getLeadEventID()
  const purchaseEventID = getPurchaseEventID()

  if (leadEventID) {
    dataLayer.push({
      event: 'cfLead',
      contactFirstName: visitorData.firstName,
      contactLastName: visitorData.lastName,
      contactEmailAddress: visitorData.emailAddress,
      contactPhoneNumber: visitorData.phoneNumber,
      contactPostalCode: visitorData.postalCode,
      contactCountry: visitorData.country,
      contactExternalID: visitorData.uuid,
      leadEventID: leadEventID,
    })
    removeLeadEventID()
  }

  if (purchaseEventID) {
    dataLayer.push({
      event: 'cfPurchase',
      contactFirstName: visitorData.firstName,
      contactLastName: visitorData.lastName,
      contactEmailAddress: visitorData.emailAddress,
      contactPhoneNumber: visitorData.phoneNumber,
      contactPostalCode: visitorData.postalCode,
      contactCountry: visitorData.country,
      contactExternalID: visitorData.uuid,
      purchaseEventID: purchaseEventID,
    })
    removePurchaseEventID()
  }
})
